import { BaseButton } from '@/src/components/buttons/BaseButton'
import { DestructiveButton } from '@/src/components/buttons/DestructiveButton'
import { SaveButton } from '@/src/components/buttons/SaveButton'
import { appColors } from '@/src/theme'
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Tooltip } from '@chakra-ui/react'
import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FlexRow } from './flex'

interface ICreateEditModal {
  header?: string | ReactNode
  isOpen?: boolean
  isCentered?: boolean
  children?: ReactNode
  onClose?: () => void
  onSave?: () => void
  size?: string
  isSaveDisabled?: boolean
  actionButtonText?: string
  actionButtonIconLeft?: ReactElement
  minWidth?: string | number
  minHeight?: string | number
  actionButtonTooltip?: string
  onDelete?: () => void
  showDeleteButton?: boolean
  hideCancelButton?: boolean
  hideSaveButton?: boolean
  footerText?: string
  footerLeftComponent?: ReactNode
  footerProps?: React.ComponentProps<typeof ModalFooter>
  footerCustomComponent?: ReactNode
  cancelButtonDataAction?: string
  saveButtonDataAction?: string
  closeButtonDataAction?: string
}

export const ActionModal = ({
  header,
  isOpen,
  children,
  onClose = () => {},
  onSave = () => {},
  size = '2xl',
  isSaveDisabled = false,
  actionButtonText,
  actionButtonIconLeft,
  isCentered = false,
  minWidth,
  minHeight,
  actionButtonTooltip,
  onDelete,
  showDeleteButton,
  hideCancelButton,
  hideSaveButton,
  footerText,
  footerLeftComponent,
  footerProps,
  footerCustomComponent,
  cancelButtonDataAction,
  saveButtonDataAction,
  closeButtonDataAction,
}: ICreateEditModal) => {
  const { t } = useTranslation()

  const renderDeleteButton = () => {
    if (!onDelete || !showDeleteButton) {
      return <></>
    }
    return (
      <DestructiveButton onClick={onDelete} marginRight={'auto'}>
        {t('iso_measures_savings_delete')}
      </DestructiveButton>
    )
  }

  return (
    <>
      <Modal isOpen={isOpen ?? false} onClose={onClose} size={size} isCentered={isCentered}>
        <ModalOverlay />
        <ModalContent minWidth={minWidth} minHeight={minHeight}>
          <ModalHeader>{header}</ModalHeader>
          <ModalCloseButton _focus={{}} data-action={closeButtonDataAction} />
          <ModalBody>{children}</ModalBody>

          <ModalFooter justifyContent='space-between' mt={'40px'} borderTopColor={'#F3F3F3'} borderTopWidth={'1px'} {...footerProps}>
            <FlexRow aria-label={t('additional_information')}>
              {!!footerText && (
                <Text color={appColors.gray['900']} fontSize={'12px'} fontWeight={400} mr={'16px'}>
                  {footerText}
                </Text>
              )}
              {footerLeftComponent}
            </FlexRow>
            <FlexRow gap='14px'>
              {renderDeleteButton()}
              {!hideCancelButton && (
                <BaseButton data-action={cancelButtonDataAction} variant='secondary' onClick={onClose}>
                  {t('common.cancel')}
                </BaseButton>
              )}
              {footerCustomComponent}
              {!hideSaveButton && (
                <Tooltip
                  label={actionButtonTooltip}
                  bg='white'
                  color='gray.800'
                  fontWeight='normal'
                  p='2'
                  borderRadius='base'
                  hasArrow
                  placement='top'
                >
                  <SaveButton
                    data-action={saveButtonDataAction}
                    iconLeft={actionButtonIconLeft}
                    isDisabled={isSaveDisabled}
                    onClick={onSave}
                  >
                    {actionButtonText ?? t('common.save')}
                  </SaveButton>
                </Tooltip>
              )}
            </FlexRow>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
